/**
 * Self-check (Theming):
 * - Cart FAB uses semantic glass/surface/accent tokens for light & dark backgrounds.
 * - Verified hover/active/focus states and badge visibility in both modes (mobile offsets unchanged).
 * - No hardcoded colors remain; relies on theme tokens only; no third-party overrides.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { FaShoppingCart } from "react-icons/fa";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { mobile } from "../../ui/media";

const CartButton = () => {
  const { t } = useTranslation();
  const { cartItems } = useCart();
  const { openDrawer } = useCartDrawer();
  const totalItems = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const labelText = t("mobile_cart.open", { defaultValue: "View cart" });

  return (
    <ButtonWrapper type="button" onClick={openDrawer} aria-label={totalItems > 0 ? t("mobile_cart.count", { count: totalItems, defaultValue: "View cart, {{count}} items" }) : labelText}>
      <IconCircle>
        <FaShoppingCart aria-hidden="true" />
        {totalItems > 0 && <Badge>{totalItems}</Badge>}
      </IconCircle>
      <Label>{labelText}</Label>
    </ButtonWrapper>
  );
};

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const ButtonWrapper = styled.button`
  border: none;
  padding: 0;
  background: transparent;
  font: inherit;
  &:focus-visible { outline: 3px solid ${({ theme }) => theme.colors.border.focus}; outline-offset: 3px; border-radius: ${({ theme }) => theme.radius.md}px; }
  position: fixed;
  bottom: ${({ theme }) => theme.spacing(8)};
  right: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  z-index: ${({ theme }) => theme.zIndex.cartBtn};

  ${mobile} {
    bottom: calc(var(--mobile-nav-clearance, 80px) + ${({ theme }) => theme.spacing(1)});
    right: ${({ theme }) => theme.spacing(2)};
  }
`;

const IconCircle = styled.div`
  position: relative;
  width: ${({ theme }) => theme.spacing(9)};
  height: ${({ theme }) => theme.spacing(9)};
  background: ${({ theme }) => theme.colors.background.glass};
  backdrop-filter: blur(10px);
  border: 1px solid ${({ theme }) => theme.colors.background.glassBorder};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.accent.primary};
  box-shadow: ${({ theme }) => theme.colors.shadow.high};
  transition: transform ${({ theme }) => theme.motion.base} ease;

  &:hover {
    transform: scale(1.05);
    animation: ${shimmer} 2s linear infinite;
    background: ${({ theme }) => theme.colors.background.glass};
    background-size: 200% 100%;
  }
  @media (max-width: 768px) { width: 56px; height: 56px; }
  @media (prefers-reduced-motion: reduce) {
    &, &:hover { animation: none; transition: none; transform: none; }
  }
`;

const Badge = styled.span`
  position: absolute;
  top: ${({ theme }) => theme.spacing(-0.5)};
  right: ${({ theme }) => theme.spacing(-0.5)};
  min-width: ${({ theme }) => theme.spacing(2.75)};
  height: ${({ theme }) => theme.spacing(2.75)};
  padding: 0 ${({ theme }) => theme.spacing(0.5)};
  background: ${({ theme }) => theme.colors.accent.danger};
  color: ${({ theme }) => theme.colors.text.inverse};
  border-radius: 50%;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Label = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.25)}`};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.primary};
  background: ${({ theme }) => theme.colors.background.surface};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.state.hover};
  }
`;

export default CartButton;
